import { useState } from "react";
import { SearchIcon, PlusIcon, XIcon } from "lucide-react";
import { AcademicianButton } from "@/components/academician/academician-button";
import { QuizTableRow, Quiz } from "@/components/academician/quiz-table-row";
import { QuizPreview } from "@/components/academician/quiz-preview";
import { Input } from "@/components/ui/input";

interface QuizLibraryPageProps {
  quizzes?: Quiz[];
  newQuizUrl?: string;
  onCreate?: () => void;
  onEdit?: (quiz: Quiz) => void;
  onDelete?: (id: string) => void;
  className?: string;
}

export function QuizLibraryPage({
  quizzes: initialQuizzes = [],
  newQuizUrl = "/admin/quizzes/new",
  onCreate,
  onEdit,
  onDelete,
  className = "",
}: QuizLibraryPageProps) {
  const [quizzes, setQuizzes] = useState(initialQuizzes);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "published" | "draft"
  >("all");
  const [previewQuiz, setPreviewQuiz] = useState<Quiz | null>(null);

  // Filter quizzes
  const filteredQuizzes = quizzes.filter((quiz) => {
    const matchesSearch =
      quiz.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quiz.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quiz.assignedTo?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || quiz.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Count by status
  const allCount = quizzes.length;
  const publishedCount = quizzes.filter((q) => q.status === "published").length;
  const draftCount = quizzes.filter((q) => q.status === "draft").length;

  const handleCreate = () => {
    if (onCreate) {
      onCreate();
    } else {
      window.location.href = newQuizUrl;
    }
  };

  const handleEdit = (quiz: Quiz) => {
    if (onEdit) {
      onEdit(quiz);
    } else {
      window.location.href = `/admin/quizzes/${quiz.id}/edit`;
    }
  };

  const handleDelete = (quiz: Quiz) => {
    if (confirm(`Are you sure you want to delete "${quiz.title}"?`)) {
      setQuizzes(quizzes.filter((q) => q.id !== quiz.id));
      onDelete?.(quiz.id);
    }
  };

  return (
    <div className={`min-h-screen bg-background ${className}`}>
      <div className="max-w-7xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Quiz Library
            </h1>
            <p className="text-lg text-muted-foreground">
              Create and manage quizzes for your learning modules
            </p>
          </div>
          <AcademicianButton
            variant="primary"
            leftIcon={<PlusIcon />}
            onClick={handleCreate}
          >
            Create Quiz
          </AcademicianButton>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search quizzes by title, description, or assignment..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filters */}
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                statusFilter === "all"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              All ({allCount})
            </button>
            <button
              onClick={() => setStatusFilter("published")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                statusFilter === "published"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              Published ({publishedCount})
            </button>
            <button
              onClick={() => setStatusFilter("draft")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                statusFilter === "draft"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              Draft ({draftCount})
            </button>
          </div>
        </div>

        {/* Quiz Table */}
        {filteredQuizzes.length > 0 ? (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                    Quiz Title
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-foreground">
                    Questions
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-foreground">
                    Passing Score
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredQuizzes.map((quiz) => (
                  <QuizTableRow
                    key={quiz.id}
                    quiz={quiz}
                    onEdit={handleEdit}
                    onPreview={(q) => setPreviewQuiz(q)}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <SearchIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No quizzes found
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {searchQuery || statusFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "Get started by creating your first quiz"}
              </p>
              {!searchQuery && statusFilter === "all" && (
                <AcademicianButton
                  variant="primary"
                  leftIcon={<PlusIcon />}
                  onClick={handleCreate}
                >
                  Create Your First Quiz
                </AcademicianButton>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewQuiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setPreviewQuiz(null)}
          />
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-background rounded-lg shadow-lg m-4">
            <button
              onClick={() => setPreviewQuiz(null)}
              className="absolute top-4 right-4 z-10 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <XIcon className="h-5 w-5" />
            </button>
            <QuizPreview
              title={previewQuiz.title}
              description={previewQuiz.description}
              questions={previewQuiz.questions || []}
              passingScore={previewQuiz.passingScore}
              onClose={() => setPreviewQuiz(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
